import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useIntelData } from '../../contexts/IntelDataContext'
import { PieChart, Pie, Cell } from 'recharts'
import { Spinner } from '../../components/Spinner'

interface PipelineStats {
  total_episodes: number
  with_transcript: number
  predictions_processed: number
  predictions_pending: number
  claims_processed: number
  claims_pending: number
  total_predictions: number
  total_claims: number
  predictions_scored: number
  predictions_unscored: number
  // Granular pending breakdown (CHO-198) — supplied by CHO-455, optional until then
  not_yet_extracted?: number
  awaiting_expiration?: number
  ready_to_score?: number
}

interface DonutCardProps {
  title: string
  processed: number
  total: number
  itemCount: number
  itemLabel: string
  linkTo?: string
  pendingLines?: Array<{ label: string; count: number; color: string }>
}

function DonutCard({ title, processed, total, itemCount, itemLabel, linkTo, pendingLines }: DonutCardProps) {
  const navigate = useNavigate()
  const pending = total - processed
  const pct = total > 0 ? Math.round((processed / total) * 100) : 0
  const color = pct === 100 ? '#10b981' : pct >= 50 ? '#3b82f6' : '#f59e0b'
  const data = [{ value: processed }, { value: pending }]

  return (
    <div
      className={`bg-white rounded-lg shadow-sm border border-gray-200 p-5 ${linkTo ? 'cursor-pointer hover:border-blue-400 hover:shadow-md transition-all' : ''}`}
      onClick={linkTo ? () => navigate(linkTo) : undefined}
      title={linkTo ? `Go to ${title}` : undefined}
    >
      <h3 className="text-sm font-semibold text-gray-700 mb-3">{title}</h3>
      <div className="flex items-center gap-4">
        <div className="relative flex-shrink-0">
          <PieChart width={80} height={80}>
            <Pie
              data={data}
              innerRadius={25}
              outerRadius={35}
              dataKey="value"
              startAngle={90}
              endAngle={-270}
              stroke="none"
            >
              <Cell fill={color} />
              <Cell fill="#e5e7eb" />
            </Pie>
          </PieChart>
          <div className="absolute inset-0 flex items-center justify-center">
            <span className="text-xs font-bold text-gray-700">{pct}%</span>
          </div>
        </div>
        <div>
          <p className="text-sm text-gray-900 font-medium">
            {processed.toLocaleString()} / {total.toLocaleString()} processed
          </p>
          <p className="text-xs text-gray-500 mt-0.5">
            {itemCount.toLocaleString()} {itemLabel} extracted
          </p>
          {pendingLines ? (
            <div className="mt-1 space-y-0.5">
              {pendingLines.map(line => (
                <p key={line.label} className={`text-xs ${line.color}`}>
                  {line.count.toLocaleString()} {line.label}
                </p>
              ))}
            </div>
          ) : pending > 0 ? (
            <p className="text-xs text-amber-600 mt-0.5">
              {pending.toLocaleString()} pending
            </p>
          ) : null}
        </div>
      </div>
    </div>
  )
}

export default function IntelPipeline() {
  const navigate = useNavigate()
  const intel = useIntelData()
  const [stats, setStats] = useState<PipelineStats | null>(
    (intel.pipelineStatsCache?.data as PipelineStats) || null
  )
  const [legacyStatus, setLegacyStatus] = useState<Record<string, number> | null>(null)
  const [legacyError, setLegacyError] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const controller = new AbortController()
    const { signal } = controller

    const fetchData = async (forceStats = false) => {
      // Stats/pipeline
      try {
        if (forceStats || intel.isStale(intel.pipelineStatsCache?.timestamp)) {
          const r = await fetch('/api/intel/stats/pipeline', { signal })
          if (!r.ok) throw new Error(`HTTP ${r.status}`)
          const data: PipelineStats = await r.json()
          setStats(data)
          intel.cachePipelineStats(data as unknown as Record<string, number>)
          setError(null)
        }
      } catch (e) {
        if ((e as Error).name === 'AbortError') return
        setError((e as Error).message)
      }

      // Pipeline/status
      try {
        const r = await fetch('/api/intel/pipeline/status', { signal })
        if (!r.ok) throw new Error(`HTTP ${r.status}`)
        const data: Record<string, number> = await r.json()
        setLegacyStatus(data)
        setLegacyError(null)
      } catch (e) {
        if ((e as Error).name === 'AbortError') return
        setLegacyError((e as Error).message)
      }
    }

    void fetchData(false)

    const interval = setInterval(() => {
      void fetchData(true)
    }, 60_000)

    return () => {
      clearInterval(interval)
      controller.abort()
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const statusMetric = (value: string | undefined): string => {
    if (legacyStatus) return value ?? '—'
    if (legacyError) return '—'
    return '...'
  }

  const stages = [
    {
      id: 'fetch-episodes',
      label: 'Fetch Episodes',
      description: 'Search YouTube channels for new episodes',
      metric: statusMetric(legacyStatus ? `${legacyStatus.episodes_fetched} episodes` : undefined),
      linkTo: '/intel/episodes',
    },
    {
      id: 'fetch-transcripts',
      label: 'Fetch Transcripts',
      description: 'Download captions or transcribe audio via Groq',
      metric: statusMetric(
        legacyStatus
          ? `${legacyStatus.episodes_transcribed}/${legacyStatus.episodes_fetched} transcribed`
          : undefined
      ),
      linkTo: '/intel/episodes',
    },
    {
      id: 'extract-predictions',
      label: 'Extract Predictions',
      description: 'Use Claude to extract predictions from transcripts',
      metric: statusMetric(legacyStatus ? `${legacyStatus.predictions_extracted} predictions` : undefined),
      linkTo: '/intel/predictions',
    },
    {
      id: 'extract-claims',
      label: 'Extract Claims',
      description: 'Use Ollama to extract factual claims from transcripts',
      metric: stats ? `${stats.total_claims.toLocaleString()} claims` : '...',
      linkTo: '/intel/claims',
    },
    {
      id: 'score-predictions',
      label: 'Score Predictions',
      description: 'Verify outcomes against market data and compute Brier scores',
      metric: stats
        ? `${stats.predictions_scored.toLocaleString()}/${stats.total_predictions.toLocaleString()} scored`
        : '...',
      linkTo: '/intel',
    },
  ]

  return (
    <div className="max-w-6xl mx-auto">
      <h2 className="text-xl sm:text-2xl font-bold mb-6 text-gray-900">Processing Pipeline</h2>

      {error && (
        <div className="bg-red-50 text-red-700 rounded-lg p-4 mb-6 text-sm">
          Failed to load pipeline stats: {error}
        </div>
      )}

      {/* CHO-196: Processing stats donut charts */}
      {!stats && !error && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="bg-white border border-gray-200 rounded-lg h-[120px] flex flex-col items-center justify-center gap-2">
              <Spinner size={24} />
              <span className="text-xs text-gray-400">Loading...</span>
            </div>
          ))}
        </div>
      )}

      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <DonutCard
            title="Transcripts"
            processed={stats.with_transcript}
            total={stats.total_episodes}
            itemCount={stats.with_transcript}
            itemLabel="transcripts"
            linkTo="/intel/episodes"
          />
          <DonutCard
            title="Predictions Extracted"
            processed={stats.predictions_processed}
            total={stats.with_transcript}
            itemCount={stats.total_predictions}
            itemLabel="predictions"
            linkTo="/intel/predictions"
            pendingLines={[
              { label: 'not yet extracted', count: stats.not_yet_extracted ?? (stats.with_transcript - stats.predictions_processed), color: 'text-gray-500' },
              { label: 'awaiting expiration', count: stats.awaiting_expiration ?? 0, color: 'text-amber-600' },
              { label: 'ready to score', count: stats.ready_to_score ?? 0, color: 'text-orange-600' },
            ].filter(l => l.count > 0)}
          />
          <DonutCard
            title="Claims Extracted"
            processed={stats.claims_processed}
            total={stats.with_transcript}
            itemCount={stats.total_claims}
            itemLabel="claims"
            linkTo="/intel/claims"
          />
          <DonutCard
            title="Predictions Scored"
            processed={stats.predictions_scored}
            total={stats.total_predictions}
            itemCount={stats.predictions_scored}
            itemLabel="scored"
          />
        </div>
      )}

      {/* Pipeline stages — each card links to its detail tab (CHO-198) */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        {stages.map((stage) => (
          <div
            key={stage.id}
            className="bg-white rounded-lg shadow-sm border border-gray-200 p-5 cursor-pointer hover:border-blue-400 hover:shadow-md transition-all"
            onClick={() => navigate(stage.linkTo)}
          >
            <div className="flex justify-between items-start">
              <div>
                <h3 className="font-semibold text-gray-900">{stage.label}</h3>
                <p className="text-sm text-gray-500 mt-1">{stage.description}</p>
                <p className="text-sm text-emerald-600 mt-2">{stage.metric}</p>
              </div>
              <span className="text-gray-300 text-lg">→</span>
            </div>
          </div>
        ))}
      </div>

      {/* Summary stats */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-5">
        <h3 className="font-semibold mb-3 text-gray-900">Summary</h3>
        {legacyStatus ? (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-center">
            <div>
              <div className="text-2xl font-bold text-emerald-600">
                {legacyStatus.episodes_fetched?.toLocaleString()}
              </div>
              <div className="text-xs text-gray-500">Episodes</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-emerald-600">
                {legacyStatus.episodes_transcribed?.toLocaleString()}
              </div>
              <div className="text-xs text-gray-500">Transcribed</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-emerald-600">
                {legacyStatus.predictions_extracted?.toLocaleString()}
              </div>
              <div className="text-xs text-gray-500">Predictions</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-emerald-600">
                {stats?.total_claims.toLocaleString() || '...'}
              </div>
              <div className="text-xs text-gray-500">Claims</div>
            </div>
          </div>
        ) : legacyError ? (
          <div className="bg-red-50 text-red-700 rounded-lg p-4 text-sm">
            Failed to load pipeline status: {legacyError}
          </div>
        ) : (
          <div className="flex items-center justify-center h-16">
            <Spinner size={24} />
          </div>
        )}
      </div>
    </div>
  )
}
